// keyboard-mode.ts — CAPABILITY de teclado virtual.
//
// PROBLEMA (reportado na LG real, 2026-07): em TV, dar foco DOM a um <input>
// abre o IME/teclado virtual da plataforma NA HORA (webOS/Tizen/Android TV/
// Fire/VIDAA). O <Input> sincronizava foco DOM com o foco ESPACIAL (Norigin):
// o D-pad pousava no campo → `element.focus()` → teclado subia sem o usuário
// pedir. Com o IME aberto, a plataforma captura as teclas e o D-pad não anda
// mais — o usuário fica PRESO no campo, sem conseguir pular pro próximo
// elemento do form.
//
// MODELO CORRETO (padrão de qualquer app de TV):
//   'deferred'  → foco espacial só DESTACA o campo. OK/Enter entra em EDIÇÃO
//                 (aí sim foco DOM → IME sobe). BACK sai da edição, devolve o
//                 foco espacial e o D-pad volta a navegar.
//   'immediate' → foco/clique dá foco DOM direto (browser/desktop/mobile, onde
//                 não existe IME modal roubando as teclas). Comportamento
//                 histórico, preservado bit a bit.
//
// É CAPABILITY, NÃO MARCA: o que decide é "esta superfície é dirigida por
// controle remoto e tem IME modal?" — vale pra LG, Samsung, Hisense, Fire TV
// ou qualquer TV futura. Nada de checar fabricante.
//
// O app pode sobrescrever via `setKeyboardMode()` (o app de TV faz isso no
// bootstrap pra que o bypass de QA `?surface=tv` também exercite o modelo de
// TV num browser de desktop). Sem override, resolve sozinho pela plataforma —
// o pacote continua auto-suficiente pra qualquer outro consumidor.

import { detectPlatform } from '@app-iptv/device'
import type { Platform } from '@app-iptv/shared'

export type KeyboardMode = 'deferred' | 'immediate'

/** Plataformas dirigidas por controle remoto, com IME modal do sistema.
 *  Focar um <input> nelas ABRE o teclado virtual e sequestra as teclas. */
const REMOTE_PLATFORMS = new Set<Platform>(['tizen', 'webos', 'androidtv', 'firetv', 'vidaa'])

/**
 * Decisão PURA: dada a plataforma, o teclado virtual deve ser adiado até o
 * usuário confirmar (OK), ou pode subir junto com o foco?
 *
 * Exportada separadamente pra ser testável determinística — `resolveKeyboardMode()`
 * (com cache + override) é o que os componentes consomem.
 */
export function computeKeyboardMode(platform: Platform): KeyboardMode {
  return REMOTE_PLATFORMS.has(platform) ? 'deferred' : 'immediate'
}

// Override explícito do app (null = detecção automática). Módulo-level, no
// mesmo espírito do `setTierOverride` do @app-iptv/device: decidido uma vez no
// boot, antes do primeiro render, e estável pela sessão.
let override: KeyboardMode | null = null
let cached: KeyboardMode | null = null

/**
 * Força o modo de teclado (ou volta pra automático com `null`).
 * O app de TV chama no bootstrap a partir da superfície resolvida, pra que o
 * bypass de QA (`?surface=tv` no desktop) exercite o modelo de TV de verdade.
 */
export function setKeyboardMode(mode: KeyboardMode | null): void {
  override = mode
  cached = null
}

/** Modo efetivo. Memoizado: a plataforma não muda no meio da sessão. */
export function resolveKeyboardMode(): KeyboardMode {
  if (override !== null) return override
  if (cached === null) cached = computeKeyboardMode(detectPlatform())
  return cached
}

/** Apenas para testes — limpa override + memo. */
export function __resetKeyboardModeCache(): void {
  override = null
  cached = null
}
